import React from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useSortable} from '@dnd-kit/sortable';
import * as css from './LectureCard.css';
import AssignedTicketsButton from './assignedTicketsButton/AssignedTicketsButton';
import LectureSticker from './lectureSticker/LectureSticker';
import LecturersDisplay from './lecturersDisplay/LecturersDisplay';
import LectureCardMenu from './lectureCardMenu/LectureCardMenu';
import DragIndicator from '../../../../assets/icons/DragIndicator';
import EditIcon from '../../../../assets/icons/EditIcon';
import {formatNumber} from '../../../../library/numberFunction';
import {TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {RootState} from '../../../../redux/store.type';

interface LectureCardProps {
    lecture: TTimetableItem;
    onTicketsClick?: () => void;
    onEdit?: () => void;
    expanded?: boolean;
    isOverlay?: boolean;
    disableDrag?: boolean;
}

function LectureCard({
    lecture,
    onTicketsClick,
    onEdit,
    expanded = false,
    isOverlay = false,
    disableDrag = false,
}: LectureCardProps) {
    const {t} = useTranslation('timetable');
    const currencySymbol = useSelector((state: RootState) => state.event.currencySymbol);
    const {title, lecturers = [], isBreak, price, amount, tickets = [], place} = lecture;

    const {attributes, listeners, setNodeRef, transform, isDragging} = useSortable({
        id: lecture.ttid,
        disabled: disableDrag,
    });
    const dragVars = {
        '--dnd-transform': transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : 'none',
    } as React.CSSProperties;
    const sourceDragging = isDragging && !isOverlay;

    return (
        <css.container $expanded={expanded} $isDragging={sourceDragging} ref={setNodeRef} style={dragVars} onDoubleClick={onEdit} {...attributes}>
            {!disableDrag && (
                <css.dragHandle {...listeners}>
                    <css.dragIcon><DragIndicator /></css.dragIcon>
                </css.dragHandle>
            )}
            <LectureSticker color={lecture.color} expanded={expanded} />
            <css.content $expanded={expanded}>
                <css.details>
                    <css.title $expanded={expanded} dir="auto">{title}</css.title>
                    {expanded ? (
                        <>
                            <LecturersDisplay lecturers={lecturers} isBreak={isBreak} />
                            <css.ticketInfo>
                                <css.ticketDetails>
                                    {price != null && (
                                        <css.detailText><css.detailLabel>{t('additionalPrice')}</css.detailLabel>{formatNumber(price)} {currencySymbol}</css.detailText>
                                    )}
                                    {amount !== undefined && (
                                        <css.detailText><css.detailLabel>{t('seatsCount')}</css.detailLabel>{amount === null ? t('unlimited') : formatNumber(amount)}</css.detailText>
                                    )}
                                    <css.detailText><css.detailLabel>{t('location')}</css.detailLabel>{place}</css.detailText>
                                </css.ticketDetails>
                                <css.actionsRow>
                                    <css.editButton
                                        type="button"
                                        onClick={onEdit}
                                        aria-label={t('editLecture')}
                                    >
                                        <EditIcon />
                                    </css.editButton>
                                    <AssignedTicketsButton
                                        count={tickets.length}
                                        onClick={onTicketsClick}
                                    />
                                </css.actionsRow>
                            </css.ticketInfo>
                        </>
                    ) : (
                        <LecturersDisplay lecturers={lecturers} isBreak={isBreak} />
                    )}
                </css.details>
                <css.menuColumn>
                    <LectureCardMenu
                        lecture={lecture}
                        onEdit={onEdit}
                    />
                    <css.statusIndicator>
                        <css.statusLabel>{lecture.active ? t('toggleActive') : t('toggleInactive')}</css.statusLabel>
                        <css.statusDot $active={!!lecture.active} />
                    </css.statusIndicator>
                </css.menuColumn>
            </css.content>
        </css.container>
    );
}

export default LectureCard;
